import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { switchMap, map, takeWhile, last, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ProvisionAnalysisRequestItem, ProvisionAnalysisResponse } from '../../objects/provision-analysis';
import { analysisProgressMessage, describeAnalysisFailure } from './document-analysis.service';

export interface ProvisionAnalysisProgress {
  attempt: number;
  maxAttempts: number;
  message: string;
}

/**
 * Calls the async provision gap-analysis endpoint (approved provisions vs. contract).
 * Mirrors {@link DocumentAnalysisService}: HTTP trigger -> poll status -> final result.
 * Same hc-document-analysis gateway; endpoints are `/contracts/{id}/provision-analysis[...]`.
 */
@Injectable({ providedIn: 'root' })
export class ProvisionAnalysisService {
  private http = inject(HttpClient);

  private readonly POLL_INTERVAL_MS = 3000;   // poll every 3s
  private readonly MAX_POLL_ATTEMPTS = 110;   // ~5.5 min; the worker's own timeout is 5 min

  /**
   * Trigger a provision gap analysis and poll until complete.
   *
   * @param analysisKey  identifies the cached analysis. For a saved contract this is its
   *                     contractId, and the backend resolves the PDF from
   *                     `contract.linkToContract`. For a document that is not (yet) a
   *                     contract — a workflow request's upload — pass that record's key
   *                     (e.g. workflowNum) together with `s3FileUrl`. It is only ever a
   *                     cache key downstream: the worker and the results table key on it
   *                     without caring what kind of record it names.
   * @param provisions   the applicable approved provisions to compare the document against
   * @param progressCallback optional polling progress (does not emit to the subscriber)
   * @param s3FileUrl    the document to analyze. Omit to analyze the contract's own
   *                     attached document. Same body key the sibling intake-analysis
   *                     endpoint takes. Two server-side constraints when it IS passed:
   *                     the url must be in THIS org's `hc-{org}-attachments` bucket (the
   *                     gateway is unauthenticated, so without it one org could have
   *                     another's contract analysed), and `analysisKey` must start with
   *                     `WF-`. The second keeps the two kinds of id from sharing a cache
   *                     row: a real contract id plus an arbitrary PDF would otherwise
   *                     overwrite that contract's saved gap analysis. Both yield a 400
   *                     whose text is surfaced to the user by `serverMessage`.
   */
  triggerProvisionAnalysis(
    analysisKey: string,
    provisions: ProvisionAnalysisRequestItem[],
    progressCallback?: (progress: ProvisionAnalysisProgress) => void,
    s3FileUrl?: string
  ): Observable<ProvisionAnalysisResponse> {
    const triggerUrl = (environment as any).URL_PROVISION_ANALYSIS_TRIGGER?.replace('{contractId}', analysisKey);
    const statusUrl = (environment as any).URL_PROVISION_ANALYSIS_STATUS?.replace('{contractId}', analysisKey);

    if (!triggerUrl || !statusUrl) {
      console.error('[ProvisionAnalysisService] API URLs not configured in environment');
      return of({ status: 'error', error: 'API endpoints not configured' } as ProvisionAnalysisResponse);
    }

    const body: { provisions: ProvisionAnalysisRequestItem[]; s3FileUrl?: string } = { provisions };
    if (s3FileUrl) body.s3FileUrl = s3FileUrl;

    return this.http.post<ProvisionAnalysisResponse>(triggerUrl, body).pipe(
      switchMap(triggerResponse => {
        // Cached/complete result comes straight back.
        if (triggerResponse.status === 'complete' || triggerResponse.status === 'cached') {
          return of(triggerResponse);
        }
        if (triggerResponse.status === 'failed' || triggerResponse.status === 'error') {
          return of(this.withFailureMessage(triggerResponse));
        }
        return this.pollForResults(statusUrl, progressCallback);
      }),
      catchError(error => {
        console.error('[ProvisionAnalysisService] trigger error:', error);
        return of({ status: 'error', error: this.serverMessage(error, 'Failed to analyze provisions') } as ProvisionAnalysisResponse);
      })
    );
  }

  /**
   * Prefer the server's own message over Angular's generic
   * "Http failure response for <url>: 400 Bad Request".
   *
   * The trigger rejects a request that names another org's bucket, a non-bucket host, a
   * malformed url, or an `s3FileUrl` paired with something that isn't a `WF-` id — each
   * with a specific 400 explaining which. Collapsing those into "Analysis failed. Please
   * try again." tells the user to retry something that will never succeed.
   */
  private serverMessage(error: any, fallback: string): string {
    return error?.error?.error || error?.error?.message || error?.message || fallback;
  }

  /**
   * A 'failed' result carries an errorCode; turn it into the user-facing `error`
   * the callers display. The raw errorMessage can be AWS text and is for logs.
   */
  private withFailureMessage(resp: ProvisionAnalysisResponse): ProvisionAnalysisResponse {
    if (resp.status !== 'failed') return resp;
    const errorCode = (resp as any).errorCode;
    console.error('[ProvisionAnalysisService] analysis failed:', errorCode, (resp as any).errorMessage);
    return { ...resp, error: describeAnalysisFailure(errorCode).message };
  }

  private pollForResults(
    statusUrl: string,
    progressCallback?: (progress: ProvisionAnalysisProgress) => void
  ): Observable<ProvisionAnalysisResponse> {
    let attempt = 0;

    return timer(0, this.POLL_INTERVAL_MS).pipe(
      switchMap(() => {
        attempt++;
        if (progressCallback) {
          progressCallback({
            attempt,
            maxAttempts: this.MAX_POLL_ATTEMPTS,
            message: analysisProgressMessage('Analyzing contract against approved provisions', attempt, this.POLL_INTERVAL_MS)
          });
        }
        return this.http.get<ProvisionAnalysisResponse>(statusUrl);
      }),
      map(response => ({ ...response, _attempt: attempt } as ProvisionAnalysisResponse & { _attempt: number })),
      // keep polling while still processing and under the cap; inclusive so the final value emits
      takeWhile(response => response.status === 'processing' && (response as any)._attempt < this.MAX_POLL_ATTEMPTS, true),
      last(),
      map(response => {
        const { _attempt, ...clean } = response as ProvisionAnalysisResponse & { _attempt?: number };
        if (clean.status === 'processing') {
          console.warn('[ProvisionAnalysisService] polling timed out');
          return { status: 'error', error: 'Analysis timed out. Please try again.' } as ProvisionAnalysisResponse;
        }
        return this.withFailureMessage(clean as ProvisionAnalysisResponse);
      }),
      catchError(error => {
        console.error('[ProvisionAnalysisService] polling error:', error);
        return of({ status: 'error', error: this.serverMessage(error, 'Failed to check analysis status') } as ProvisionAnalysisResponse);
      })
    );
  }
}
